import StructuredStorage from '@tencent/memoire-structured-storage';
import IDBEngine from '@tencent/memoire-idb-engine';
import LoggerPlugin from '@tencent/memoire-plugin-logger';
import { DATABASE_NAME, DATABASE_VERSION } from '../constants';
import { MutationsPacksModel } from './mutations-packs-model';
import type { IMutationsPackExtended } from '../persistent';
import type { Collection } from '@tencent/memoire-structured-storage';
import type { ILogger } from '@tencent/slide-sdk-model/src/manager/logger.interface';

export class MutationsPacksStorage {
    private storage: StructuredStorage;
    private store: Collection<MutationsPacksModel>;

    public constructor(private logger: ILogger, private key: string, private moduleName: string) {
        this.storage = this.createStorage();
        this.store = this.storage.collection(MutationsPacksModel);
    }

    public open(): Promise<void> {
        return this.storage.open();
    }

    public async get(): Promise<IMutationsPackExtended[]> {
        const data = await this.store.get(this.key);
        return data?.getMutationsPacks() ?? [];
    }

    public async delete(): Promise<void> {
        await this.store.delete(this.key);
    }

    public async upsert(packs: IMutationsPackExtended[]): Promise<void> {
        await this.store.upsert(new MutationsPacksModel({ id: this.key, mutationsPacks: packs }));
    }

    public entry() {
        return this.store.entries();
    }

    public clear() {
        return this.store.clear();
    }

    public bulkUpsert(data: Array<{ id: string; mutationsPacks: IMutationsPackExtended[] }>) {
        return this.store.bulkUpsert(
            data.map(({ id, mutationsPacks }) => new MutationsPacksModel({ id, mutationsPacks })),
        );
    }

    private createStorage(): StructuredStorage {
        const storage = new StructuredStorage({
            name: DATABASE_NAME,
            version: DATABASE_VERSION,
            engine: new IDBEngine(),
            plugins: [
                new LoggerPlugin({
                    logger: this.logger,
                    verbose: false,
                    prefix: this.moduleName,
                }),
            ],
            disableAutoOpen: true,
        });
        storage.events.onFetal((err) => {
            this.logger.error(this.moduleName, err);
        });
        storage.events.onError((err) => {
            this.logger.warn(this.moduleName, err);
        });

        return storage;
    }
}
